// Equine Hydration Estimator (EHE) — ported from dissertation Appendix 1
// Equation: WL_drink = WL_total - (WL_feed + WL_meta)
// Dehydration: ((WL_total - WL_actual) / BW) * 100

export type EheFeedType = "gross hay" | "dried beet pulp" | "wet beet pulp" | "grain";
export type Intensity = "rest" | "light" | "medium" | "intense";
export type UiFeedType = "Hay" | "Haylage" | "Grass" | "Mix" | "Hard feed";
export type StatusKey = "hydrated" | "mild" | "moderate" | "severe" | "critical" | "idle";

type RateTable = Record<number, Record<Intensity, number>>;

const SWEATING_RATES: RateTable = {
  10: { rest: 0.00,  light: 1.5,   medium: 2.25,   intense: 4.5   },
  20: { rest: 0.00,  light: 1.50,  medium: 2.25,   intense: 4.5   },
  30: { rest: 0.22,  light: 1.98,  medium: 2.925,  intense: 5.85  },
  35: { rest: 0.30,  light: 2.175, medium: 3.2625, intense: 6.525 },
};

const RESPIRATION_RATES: RateTable = {
  10: { rest: 0.024, light: 0.50,  medium: 0.75,   intense: 1.50  },
  20: { rest: 0.024, light: 0.50,  medium: 0.75,   intense: 1.50  },
  30: { rest: 0.031, light: 0.65,  medium: 0.975,  intense: 1.95  },
  35: { rest: 0.035, light: 0.729, medium: 1.0875, intense: 2.175 },
};

const URINATION_FECAL_TOTALS: Record<string, number> = {
  "oil-hay":       33.5,
  "wet beet pulp": 20.5,
  "dry beet pulp": 18.0,
  "pure grain":    18.4,
};

export const FEED_WATER_CONTENT: Record<EheFeedType, number> = {
  "gross hay":       0.175,
  "dried beet pulp": 0.105,
  "wet beet pulp":   0.81,
  "grain":           0.13,
};

const FEED_TO_FECAL_MAP: Record<EheFeedType, string> = {
  "gross hay":       "oil-hay",
  "dried beet pulp": "dry beet pulp",
  "wet beet pulp":   "wet beet pulp",
  "grain":           "pure grain",
};

const TEAR_RATE = 0.005;
const METABOLIC_WATER_RATE = 0.005;

export const UI_FEED_TYPES: readonly UiFeedType[] = ["Hay", "Haylage", "Grass", "Mix", "Hard feed"] as const;

export function mapUiFeedToEhe(uiType: UiFeedType | string): EheFeedType {
  switch (uiType) {
    case "Hay":       return "gross hay";
    case "Haylage":   return "gross hay";
    case "Grass":     return "wet beet pulp";
    case "Mix":       return "grain";
    case "Hard feed": return "grain";
    default:          return "gross hay";
  }
}

export function statusKey(status: string | null | undefined): StatusKey {
  if (!status) return "idle";
  const s = status.toLowerCase();
  if (s.startsWith("critical")) return "critical";
  if (s.startsWith("severe"))   return "severe";
  if (s.startsWith("moderate")) return "moderate";
  if (s.startsWith("mild"))     return "mild";
  return "hydrated";
}

function interpolateRate(temp: number, ratesObj: RateTable, intensity: Intensity): number {
  const points = Object.keys(ratesObj).map(Number).sort((a, b) => a - b);
  const first = points[0]!;
  const last = points[points.length - 1]!;
  if (temp <= first) return ratesObj[first]![intensity];
  if (temp >= last) return ratesObj[last]![intensity];
  for (let i = 0; i < points.length - 1; i++) {
    const t1 = points[i]!;
    const t2 = points[i + 1]!;
    if (temp >= t1 && temp <= t2) {
      const r1 = ratesObj[t1]![intensity];
      const r2 = ratesObj[t2]![intensity];
      return r1 + (r2 - r1) * (temp - t1) / (t2 - t1);
    }
  }
  return ratesObj[first]![intensity];
}

export interface FeedItem {
  type: EheFeedType;
  amount: number;
}

export interface WaterLossInput {
  weight: number;
  exerciseDurationHrs: number;
  exerciseIntensity: Intensity;
  temperature: number;
  feedList: FeedItem[];
}

export function calculateWaterLoss({ weight, exerciseDurationHrs, exerciseIntensity, temperature, feedList }: WaterLossInput): number {
  const restDuration = 24 - exerciseDurationHrs;
  const intensity: Intensity = exerciseDurationHrs === 0 ? "rest" : exerciseIntensity;

  const sweatRest = interpolateRate(temperature, SWEATING_RATES, "rest");
  const sweatEx   = exerciseDurationHrs === 0 ? 0 : interpolateRate(temperature, SWEATING_RATES, intensity);
  const wlSweat   = (sweatEx * exerciseDurationHrs + sweatRest * restDuration) * (weight / 100);

  const respRest = interpolateRate(temperature, RESPIRATION_RATES, "rest");
  const respEx   = exerciseDurationHrs === 0 ? 0 : interpolateRate(temperature, RESPIRATION_RATES, intensity);
  const wlResp   = (respEx * exerciseDurationHrs + respRest * (exerciseDurationHrs === 0 ? 24 : restDuration)) * (weight / 100);

  const wlTear = TEAR_RATE * weight;

  const totalFeed = feedList.reduce((sum, f) => sum + f.amount, 0);
  let wlUF = 0;
  if (totalFeed > 0) {
    let weightedUF = 0;
    feedList.forEach(({ type, amount }) => {
      const base = URINATION_FECAL_TOTALS[FEED_TO_FECAL_MAP[type]];
      if (base !== undefined) weightedUF += base * (amount / totalFeed);
    });
    wlUF = weightedUF * (weight / 500);
  }

  return wlSweat + wlResp + wlTear + wlUF;
}

export interface DrinkingWaterInput {
  wlTotal: number;
  weight: number;
  feedList: FeedItem[];
}

export interface DrinkingWaterResult {
  wlDrink: number;
  wlFeed: number;
  wlMeta: number;
}

export function calculateDrinkingWater({ wlTotal, weight, feedList }: DrinkingWaterInput): DrinkingWaterResult {
  const wlFeed = feedList.reduce((sum, { type, amount }) => sum + amount * FEED_WATER_CONTENT[type], 0);
  const wlMeta = METABOLIC_WATER_RATE * weight;
  const wlDrink = wlTotal - wlFeed - wlMeta;
  return { wlDrink: Math.max(0, wlDrink), wlFeed, wlMeta };
}

export interface DehydrationInput {
  wlTotal: number;
  wlFeed: number;
  wlMeta: number;
  actualDrunk: number;
  weight: number;
}

export interface DehydrationResult {
  percentage: string;
  status: string;
  colour: string;
}

export function calculateDehydration({ wlTotal, wlFeed, wlMeta, actualDrunk, weight }: DehydrationInput): DehydrationResult {
  const wgTotal = wlFeed + wlMeta + actualDrunk;
  const pct = ((wlTotal - wgTotal) / weight) * 100;

  let status: string;
  let colour: string;
  if (pct <= 2.9)       { status = "Hydrated";             colour = "#16a34a"; }
  else if (pct <= 4.9)  { status = "Mild Dehydration";     colour = "#ca8a04"; }
  else if (pct <= 6.4)  { status = "Moderate Dehydration"; colour = "#ea580c"; }
  else if (pct <= 7.9)  { status = "Severe Dehydration";   colour = "#dc2626"; }
  else                  { status = "Critical Dehydration"; colour = "#7f1d1d"; }

  return { percentage: Math.max(0, pct).toFixed(1), status, colour };
}
